
import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Leaf, 
  TrendingUp, 
  AlertTriangle, 
  Search, 
  Info, 
  Globe, 
  Activity, 
  Download, 
  ShieldCheck,
  Zap
} from 'lucide-react';

export const ESGView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [query, setQuery] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [report, setReport] = useState<any>(null);

  const handleFetch = () => {
    if (!query) return;
    setIsFetching(true);
    setTimeout(() => {
      setReport({
        entity: query,
        rating: 'A+',
        overallScore: 92,
        environmental: 88,
        social: 94,
        governance: 95,
        trend: '+12%',
        riskLevel: 'Low',
        lastAudit: '28 Oct 2023',
        factors: [
          { name: 'Carbon Footprint', status: 'Optimal', score: 90 },
          { name: 'Labor Standards', status: 'Good', score: 85 },
          { name: 'Board Diversity', status: 'Exceptional', score: 98 }
        ]
      });
      setIsFetching(false);
    }, 1500);
  };

  return (
    <div className="p-6 space-y-8 animate-in slide-in-from-right duration-500 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-600">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">ESG Intelligence</h1>
            <p className="text-sm text-slate-500 font-medium">Environmental, Social & Governance risk modeling via Opoint.</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-white border border-slate-200 text-slate-700 px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 shadow-sm transition-all">
            Sustainability Standard ISO-26000
          </button>
        </div>
      </div>

      <div className="bg-slate-900 p-8 rounded-[3rem] shadow-2xl relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-white text-lg font-black mb-6 flex items-center">
            <Search size={18} className="mr-2 text-emerald-400" /> Sustainability Audit Fetcher
          </h2>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <input 
                type="text" 
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Corporate Entity (e.g., Patagonia S.A.)" 
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-12 py-4 text-white outline-none focus:ring-4 focus:ring-emerald-500/20 transition-all font-medium"
              />
            </div>
            <button 
              onClick={handleFetch}
              disabled={isFetching}
              className="bg-emerald-600 text-white px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-50"
            >
              {isFetching ? 'Processing Opoint API...' : 'Generate ESG Report'}
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-600 rounded-full blur-[100px] opacity-10 -translate-x-1/2 translate-y-1/2"></div>
      </div>

      {report ? (
        <div className="space-y-8 animate-in zoom-in-95 duration-500">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <MetricCard icon={<Leaf className="text-emerald-600" />} label="Overall Rating" value={report.rating} sub={`Score: ${report.overallScore}/100`} />
            <MetricCard icon={<TrendingUp className="text-blue-600" />} label="Quarterly Trend" value={report.trend} sub="Improving" />
            <MetricCard icon={<ShieldCheck className="text-indigo-600" />} label="Audit Status" value="Passed" sub={report.lastAudit} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
               <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-8 flex items-center">
                  <Activity size={16} className="mr-2 text-emerald-600" /> Domain Score Breakdown
               </h3>
               <div className="space-y-8">
                  <ScoreProgress label="Environmental" score={report.environmental} color="bg-emerald-500" />
                  <ScoreProgress label="Social Responsibility" score={report.social} color="bg-blue-500" />
                  <ScoreProgress label="Corporate Governance" score={report.governance} color="bg-indigo-500" />
               </div>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm flex flex-col">
               <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-6 flex items-center">
                  <Zap size={16} className="mr-2 text-amber-500" /> Key Material Factors
               </h3>
               <div className="flex-1 space-y-4">
                  {report.factors.map((f: any, i: number) => (
                    <div key={i} className="p-4 bg-slate-50 border border-slate-100 rounded-2xl flex justify-between items-center group hover:bg-white hover:border-emerald-200 transition-all">
                       <div>
                          <p className="text-xs font-black text-slate-900">{f.name}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{f.status}</p>
                       </div>
                       <span className="text-sm font-black text-emerald-600">{f.score}%</span>
                    </div>
                  ))}
               </div>
               <button className="w-full mt-6 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center justify-center space-x-2">
                  <Download size={14} />
                  <span>Download ESG Certificate</span>
               </button>
            </div>
          </div>
        </div>
      ) : (
        !isFetching && (
          <div className="bg-white rounded-[3rem] border border-slate-200 border-dashed p-20 text-center flex flex-col items-center justify-center opacity-40">
            <Leaf size={48} className="text-slate-200 mb-4" />
            <p className="text-xs font-black uppercase tracking-widest text-slate-400">Search for a company to view ESG metrics.</p>
          </div>
        )
      )}
    </div>
  );
};

const MetricCard: React.FC<{ icon: React.ReactNode, label: string, value: string, sub: string }> = ({ icon, label, value, sub }) => (
  <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm group hover:border-emerald-300 transition-all">
    <div className="p-3 bg-slate-50 rounded-2xl w-fit mb-4 group-hover:bg-emerald-50 transition-colors">{icon}</div>
    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</p>
    <p className="text-3xl font-black text-slate-900 mb-1">{value}</p>
    <p className="text-xs text-slate-500 font-medium">{sub}</p>
  </div>
);

const ScoreProgress: React.FC<{ label: string, score: number, color: string }> = ({ label, score, color }) => (
  <div className="space-y-3">
     <div className="flex justify-between items-end">
        <span className="text-xs font-black text-slate-700 uppercase tracking-widest">{label}</span>
        <span className="text-sm font-black text-slate-900">{score}%</span>
     </div>
     <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
        <div className={`${color} h-full transition-all duration-1000`} style={{ width: `${score}%` }}></div>
     </div>
  </div>
);
